/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { CourseFinancialDetailViewModel } from "../types/CourseFinancialDetailViewModel.ts";
import { courseFinancialCostCenterViewModelSchema } from "./courseFinancialCostCenterViewModelSchema.ts";
import { courseFinancialLangViewModelSchema } from "./courseFinancialLangViewModelSchema.ts";
import { courseFinancialPaymentMethodViewModelSchema } from "./courseFinancialPaymentMethodViewModelSchema.ts";
import { ePaymentReceiptTypeSchema } from "./ePaymentReceiptTypeSchema.ts";

export const courseFinancialDetailViewModelSchema = z.object({
  id: z.optional(z.number().int().describe("ID do plano financeiro.")),
  courseId: z.optional(z.number().int().describe("ID do curso.")),
  courseName: z
    .string()
    .describe(
      "Nome oficial do curso no idioma corrente, com fallback para o idioma padrão.",
    )
    .nullish(),
  courseCode: z.string().describe("Código do curso.").nullish(),
  courseCompanyId: z
    .number()
    .int()
    .describe("ID da empresa do curso. Nulo para cursos da matriz.")
    .nullish(),
  courseRequiredApproval: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso exige aprovação de documentação. Quando verdadeiro, a taxa de inscrição é obrigatória.",
      ),
  ),
  courseInternational: z.optional(
    z.boolean().describe("Indica se o curso é internacional."),
  ),
  bankId: z.number().int().describe("ID do banco do plano.").nullish(),
  bankName: z.string().describe("Nome do banco do plano.").nullish(),
  bankAllowAprovaFacil: z.optional(
    z
      .boolean()
      .describe("Indica se o banco aceita cartão de crédito AprovaFácil."),
  ),
  bankAllowMundiPagg: z.optional(
    z
      .boolean()
      .describe("Indica se o banco aceita cartão de crédito MundiPagg."),
  ),
  bankEnablePecegePay: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o banco tem PecegePay habilitado. Quando falso, as classificações contábeis e o rateio de centros de custo não são gravados.",
      ),
  ),
  bankForceCreditCardPecegePay: z.optional(
    z
      .boolean()
      .describe(
        "Indica que o banco força o cartão de crédito PecegePay. No legado, essa flag exibia os campos contábeis mesmo em banco sem PecegePay.",
      ),
  ),
  bankForceBilletPecegePay: z.optional(
    z.boolean().describe("Indica que o banco força o boleto PecegePay."),
  ),
  description: z.string().describe("Descrição do plano.").nullish(),
  startDate: z.optional(
    z.string().datetime().describe("Início da vigência do plano."),
  ),
  endDate: z
    .string()
    .datetime()
    .describe(
      "Fim da vigência do plano, normalizado para 23:59:59 do dia informado. Opcional.",
    )
    .nullish(),
  active: z.optional(z.boolean().describe("Indica se o plano está ativo.")),
  inactiveDate: z
    .string()
    .datetime()
    .describe("Data em que o plano foi inativado. Opcional.")
    .nullish(),
  totalValue: z.number().describe("Valor total do plano. Opcional.").nullish(),
  valueRegistration: z
    .number()
    .describe("Taxa de inscrição. Opcional.")
    .nullish(),
  interest: z
    .number()
    .describe("Juros ao mês, em percentual. Opcional.")
    .nullish(),
  fine: z
    .number()
    .describe("Multa por atraso, em percentual. Opcional.")
    .nullish(),
  discountPunctualPercentage: z
    .number()
    .describe(
      "Desconto de pontualidade, em percentual. Gravado na coluna DISCOUNT. Não é bloqueado quando há matrículas.",
    )
    .nullish(),
  inCashDiscount: z.optional(
    z.number().describe("Desconto à vista, em percentual."),
  ),
  showInCashDiscount: z.optional(
    z.boolean().describe("Indica se o desconto à vista é exibido ao aluno."),
  ),
  extendExpiration: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o vencimento é prorrogado quando cai em dia não útil.",
      ),
  ),
  disableUser: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o aluno inadimplente é desabilitado. Quando falso, os campos de dias e parcelas para desabilitar são ignorados.",
      ),
  ),
  daysToDisable: z.optional(
    z.number().int().describe("Dias de atraso para desabilitar o aluno."),
  ),
  daysToDisableRenegotiation: z
    .number()
    .int()
    .describe(
      "Dias de atraso para desabilitar o aluno em renegociação. Opcional.",
    )
    .nullish(),
  parcelsToDisable: z
    .number()
    .int()
    .describe("Parcelas em atraso para desabilitar o aluno. Opcional.")
    .nullish(),
  parcelsToDisableRenegotiation: z
    .number()
    .int()
    .describe(
      "Parcelas em atraso para desabilitar o aluno em renegociação. Opcional.",
    )
    .nullish(),
  instruction1: z
    .string()
    .describe(
      "Primeira instrução do boleto. Quando não informada, assume a instrução padrão do banco.",
    )
    .nullish(),
  instruction2: z
    .string()
    .describe(
      "Segunda instrução do boleto. Quando não informada, assume a instrução padrão do banco.",
    )
    .nullish(),
  instruction3: z
    .string()
    .describe(
      "Terceira instrução do boleto. Quando não informada, assume a instrução padrão do banco.",
    )
    .nullish(),
  message: z
    .string()
    .describe("Mensagem livre impressa no boleto. Opcional.")
    .nullish(),
  trailOnly: z.optional(
    z
      .boolean()
      .describe(
        "Indica que o plano só pode ser usado em matrícula por trilha. Campo oculto na tela legada.",
      ),
  ),
  dateStartPayment: z
    .string()
    .datetime()
    .describe(
      "Data de início dos pagamentos. Obrigatória quando o vencimento da primeira parcela é forçado.",
    )
    .nullish(),
  preventFuturePayment: z.optional(
    z
      .boolean()
      .describe("Indica se pagamentos com vencimento futuro são bloqueados."),
  ),
  forceFirstInstallment: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a data de vencimento da primeira parcela é forçada. Mutuamente exclusivo com DaysToFirstParcelMaturity.",
      ),
  ),
  daysToFirstParcelMaturity: z
    .number()
    .int()
    .describe(
      "Dias para o vencimento da primeira parcela. Nulo quando o vencimento é forçado.",
    )
    .nullish(),
  daysToRegistrationMaturity: z
    .number()
    .int()
    .describe("Dias para o vencimento da taxa de inscrição. Opcional.")
    .nullish(),
  enableDefaultingBlock: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o bloqueio de alunos inadimplentes está ativo. Quando falso, o período de verificação e os dias para bloqueio são ignorados.",
      ),
  ),
  periodDefaultingCheck: z
    .number()
    .int()
    .describe(
      "Período de verificação de inadimplência, em meses. Entre 1 e 24.",
    )
    .nullish(),
  daysToBlock: z
    .number()
    .int()
    .describe("Dias de atraso para bloquear o aluno. Entre 0 e 360.")
    .nullish(),
  noPunctualityDiscountAfterExpiration: z.optional(
    z
      .boolean()
      .describe(
        "Indica que o desconto de pontualidade não vale após o vencimento.",
      ),
  ),
  discountAutomatic: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o desconto automático do aluno está ativo. Campo oculto na tela legada.",
      ),
  ),
  discountAutomaticPercentage: z
    .number()
    .describe(
      "Percentual do desconto automático. Nulo quando o desconto automático está desligado.",
    )
    .nullish(),
  discountAutomaticAccumulateCoupon: z.optional(
    z.boolean().describe("Indica se o desconto automático acumula com cupom."),
  ),
  discountAutomaticAccumulateInCash: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o desconto automático acumula com o desconto à vista.",
      ),
  ),
  internationalPlan: z.optional(
    z.boolean().describe("Indica se é um plano internacional."),
  ),
  showRealToDollarConversion: z.optional(
    z
      .boolean()
      .describe("Indica se a conversão de real para dólar é exibida ao aluno."),
  ),
  indicatorProgramDiscountValue: z
    .number()
    .describe("Valor do desconto do programa aluno indica aluno. Opcional.")
    .nullish(),
  paymentReceiptType: z.optional(
    z
      .lazy(() => ePaymentReceiptTypeSchema)
      .describe("1 = Invoice | 2 = Receipt | "),
  ),
  registrationCodeFealq: z
    .string()
    .describe("Código do projeto de inscrição na fundação. Opcional.")
    .nullish(),
  paymentCodeFealq: z
    .string()
    .describe("Código do projeto de mensalidade na fundação. Opcional.")
    .nullish(),
  classificationAccount: z
    .string()
    .describe("GUID da classificação contábil no PecegePay. Opcional.")
    .nullish(),
  paymentAccount: z
    .string()
    .describe("GUID da conta de pagamento no PecegePay. Opcional.")
    .nullish(),
  classificationUSP: z
    .string()
    .describe("GUID da classificação USP. Opcional.")
    .nullish(),
  classificationEnrollmentUSP: z
    .string()
    .describe("GUID da classificação USP de inscrição. Opcional.")
    .nullish(),
  hasEnrollment: z.optional(
    z
      .boolean()
      .describe("Indica se o plano já foi escolhido por alguma matrícula."),
  ),
  hasTrailLink: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o plano está vinculado a trilhas de turmas. Quando verdadeiro, a exclusão é bloqueada.",
      ),
  ),
  canBeDeleted: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o plano pode ser excluído. Falso quando há vínculo com trilha.",
      ),
  ),
  blockedFields: z
    .array(z.string())
    .describe(
      "Campos que não podem ser alterados porque o plano já possui matrículas. Vazio quando não há matrícula.",
    )
    .nullish(),
  creationDate: z
    .string()
    .datetime()
    .describe("Data de criação. Opcional.")
    .nullish(),
  userCreation: z
    .string()
    .describe("Usuário responsável pela criação. Opcional.")
    .nullish(),
  lastChangeDate: z
    .string()
    .datetime()
    .describe("Data da última alteração. Opcional.")
    .nullish(),
  userLastChange: z
    .string()
    .describe("Usuário responsável pela última alteração. Opcional.")
    .nullish(),
  paymentMethods: z
    .array(z.lazy(() => courseFinancialPaymentMethodViewModelSchema))
    .describe("Formas de pagamento configuradas no plano.")
    .nullish(),
  costCenters: z
    .array(z.lazy(() => courseFinancialCostCenterViewModelSchema))
    .describe(
      "Rateio de centros de custo. Quando informado, os percentuais somam exatamente 100.",
    )
    .nullish(),
  langs: z
    .array(z.lazy(() => courseFinancialLangViewModelSchema))
    .describe("Descrições do plano por idioma.")
    .nullish(),
  benefitsContemplated: z
    .array(z.number().int())
    .describe("IDs dos benefícios contemplados pelo plano.")
    .nullish(),
  benefitsNotContemplated: z
    .array(z.number().int())
    .describe("IDs dos benefícios não contemplados pelo plano.")
    .nullish(),
}) as unknown as ToZod<CourseFinancialDetailViewModel>;
